import { lazy, Suspense, useMemo, useState } from "react";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  usePopupTimings,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import type {
  CustomHomePopupItem,
  CustomHomePopupResult,
} from "./useCustomHomePopup.types";
import { useUserDataFlattened, useUserStore } from "@/modules";

const CustomHomePopup = lazy(() =>
  import("../../CustomHomePopup/CustomHomePopup").then(m => ({
    default: m.CustomHomePopup,
  }))
);

const CUSTOM_HOME_POPUP_CONFIG = POPUP_CONFIGS.CUSTOM_HOME_POPUP;

export const useCustomHomePopup = (): CustomHomePopupResult => {
  const popupTimings = usePopupTimings();
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  // 获取进入首页的自定义弹窗数据
  const customItems = useMemo(() => {
    if (!popupTimings) return [];

    const enterHomePopup = popupTimings.find(
      timing => timing.timingType === "ENTER_HOME_PAGE"
    );

    if (!enterHomePopup || !enterHomePopup.enabled) return [];

    const items = (enterHomePopup.customItems || [])
      .filter(item => item.enabled)
      .sort((a, b) => a.sort - b.sort);
    return items as CustomHomePopupItem[];
  }, [popupTimings]);

  const hasItems = customItems.length > 0;

  // 获取优先级：优先使用 customItems 中第一个的 sort 值，否则使用默认值
  const priority = CUSTOM_HOME_POPUP_CONFIG.priority;

  const CustomHomePopupContent: React.FC<{ onComplete: () => void }> = ({
    onComplete,
  }) => {
    const [visible, setVisible] = useState(true);

    const handleClose = () => {
      setVisible(false);
      onComplete();
    };

    return (
      <Suspense fallback={null}>
        <CustomHomePopup
          visible={visible}
          items={customItems}
          onClose={handleClose}
        />
      </Suspense>
    );
  };

  const { trigger } = useQueuedPopup({
    popupId: CUSTOM_HOME_POPUP_CONFIG.id,
    priority,
    shouldShow: () => isConfigReady && hasItems,
    renderContent: onComplete => (
      <CustomHomePopupContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: false,
    autoTrigger: true,
    // userId 入依赖：游客 → 登录、或同设备换登录另一个用户时都要重新评估，
    // 让新身份当天也能弹一次（记录按 popupId_userId 分身份存储）
    dependencies: [
      isConfigReady,
      customItems.length,
      isAuthenticated,
      userInfo?.userId,
    ],
    userBinding: true,
    // 身份必须取 store 里的原始 userInfo，且在调用的那一刻现读：
    // 1) useUserDataFlattened().data 是 userInfo/extras/realtime 的合并结果，
    //    只要 extras/realtime 的响应里带了 userId 字段（哪怕是 null），
    //    合并后 data.userId 就会被覆盖掉，而 isAuthenticated 读的是原始 userInfo，
    //    两者会短暂对不上——展示时被当成游客记到 anonymous key，
    //    关闭后再按真实 userId 判定“还没弹过”，于是连弹两次；
    // 2) 现读也避免闭包里拿到某次渲染的旧快照。
    getUserId: () => useUserStore.getState().userInfo?.userId,
  });

  return {
    items: customItems,
    hasItems,
    trigger,
  };
};
